import { useCallback, useEffect, useMemo, useState } from 'react';
import { buildPlatformScope } from '@examflow/core';
import {
  getLevelOptionsForExam,
  getWritingTaskOptionsForExam,
  isLevelSupportedForExam,
  type WritingTaskOption,
} from '@examflow/exams';
import {
  buildExamTrainingEntryPageModel,
  buildAttemptHref,
  isOpenResponseSkillId,
  summarizeWritingEvidence,
  type ExamCode,
  type LearnerContentReadinessInput,
  type LearnerContentReadinessStatus,
  type PageAction,
  type TcfModuleId,
  type TrainingIntensity,
  type UserLearningContext,
  type WritingEvidenceSummary,
} from '@examflow/learning-space';
import { getTrainingDifficulty, setTrainingDifficulty } from '../../lib/userPreferences';
import {
  buildTrainingRecordRecommendation,
  type TrainingRecordRecommendation,
} from './trainingRecordRecommendation';
import {
  buildLearningEvidenceRecordQueryScope,
  useRecentTrainingAttempts,
  type RecentTrainingAttemptsStatus,
} from './useRecentTrainingAttempts';
import { useIncompleteTrainingCard } from './useIncompleteTrainingCard';
import { abandonQuizSession } from '../quiz/quizSessionRuntime';
import { openQuizRuntimeHref } from '../quiz/quizRuntimeNavigation';

/** Learner entry no longer picks intensity; choice Skills always launch at this preset. */
export const DEFAULT_TRAINING_ENTRY_INTENSITY: TrainingIntensity = 'standard';

export const CHOICE_SESSION_SIZE_SUMMARY = '约 12 题 · 随时可停';
export const OPEN_RESPONSE_SESSION_SUMMARY = '1 题 · 随时可停';

/** Append local draft resume pin without inventing a second AttemptSpec field. */
export function appendResumeQuestionId(
  href: string | undefined,
  questionId: string | null | undefined
): string | undefined {
  if (!href || !questionId?.trim()) return href;
  const qIndex = href.indexOf('?');
  const path = qIndex >= 0 ? href.slice(0, qIndex) : href;
  const query = qIndex >= 0 ? href.slice(qIndex + 1) : '';
  const params = new URLSearchParams(query);
  params.set('resumeQuestionId', questionId.trim());
  return `${path}?${params.toString()}`;
}

function resolveTrainingLanguageId(viewer: UserLearningContext, contentPack: ExamCode): string {
  return (
    buildPlatformScope({ examType: viewer.activeExamSpace.examCode })?.languageId ??
    buildPlatformScope({ examType: contentPack })?.languageId ??
    'french'
  );
}

function resolveInitialLevel(examType: ExamCode, preferredLevel?: string | null): string {
  const preferred = preferredLevel?.trim() ?? '';
  if (preferred && isLevelSupportedForExam(examType, preferred)) {
    return preferred;
  }
  const stored = getTrainingDifficulty(examType);
  if (stored && isLevelSupportedForExam(examType, stored)) {
    return stored;
  }
  return '';
}

export function buildTrainingEntryPrimaryAction({
  baseAction,
  contentPack,
  languageId,
  selectedModuleId,
  selectedLevel,
  selectedTaskNumber,
  selectedModuleDisabled,
  selectedModuleDisabledReason,
  recommendation,
  recommendationStatus,
  contentReadinessStatus,
}: {
  baseAction: PageAction;
  contentPack: ExamCode;
  languageId: string;
  selectedModuleId: TcfModuleId;
  selectedLevel: string;
  selectedTaskNumber?: number | null;
  selectedModuleDisabled?: boolean;
  selectedModuleDisabledReason?: string | null;
  recommendation: TrainingRecordRecommendation;
  recommendationStatus: RecentTrainingAttemptsStatus;
  contentReadinessStatus?: LearnerContentReadinessStatus;
}): {
  action: PageAction;
  recommendedModuleId: TcfModuleId | null;
  /** When writing is selected and prescription targets another Skill — never steals primary. */
  secondaryAction: PageAction | null;
  secondarySelectModuleId: TcfModuleId | null;
} {
  const selectedIntensity = DEFAULT_TRAINING_ENTRY_INTENSITY;
  if (contentReadinessStatus && contentReadinessStatus !== 'ready') {
    return {
      action: baseAction,
      recommendedModuleId: null,
      secondaryAction: null,
      secondarySelectModuleId: null,
    };
  }

  if (selectedModuleDisabled) {
    return {
      action: {
        id: 'start-training-session-blocked',
        label: '暂不可开始',
        kind: 'primary',
        href: undefined,
        disabled: true,
        disabledReason:
          selectedModuleDisabledReason ??
          '当前科目暂不可开始训练。请改选其他已开放科目。',
      },
      recommendedModuleId: null,
      secondaryAction: null,
      secondarySelectModuleId: null,
    };
  }

  // Writing: primary always「开始写」. Entry 不挂草稿 CTA；Quiz 对 writing 无 sessionId 时：
  // 同指纹未交卷 → 静默续同一题（不弹窗、不重抽）；交卷后下次才 create。
  if (selectedModuleId === 'writing') {
    const href = buildAttemptHref({
      kind: 'training',
      languageId,
      examType: contentPack,
      skillId: 'writing',
      intensity: selectedIntensity,
      level: selectedLevel || undefined,
      taskNumber: selectedTaskNumber || undefined,
    });
    const otherModulePrescription =
      recommendationStatus === 'ready' && recommendation.targetModuleId !== 'writing';
    return {
      action: {
        ...baseAction,
        id: 'start-writing-session',
        label: '开始写',
        href,
      },
      recommendedModuleId: null,
      secondaryAction: otherModulePrescription
        ? {
            id: 'also-train-recommended-module',
            label: `也练${recommendation.targetModuleLabel}`,
            kind: 'secondary',
            href: undefined,
          }
        : null,
      secondarySelectModuleId: otherModulePrescription
        ? recommendation.targetModuleId
        : null,
    };
  }

  if (recommendationStatus === 'ready' && recommendation.targetModuleId !== selectedModuleId) {
    return {
      action: {
        id: 'start-recommended-training',
        label: `按处方训练${recommendation.targetModuleLabel}`,
        kind: 'primary',
        href: recommendation.sessionHref,
      },
      recommendedModuleId: null,
      secondaryAction: null,
      secondarySelectModuleId: null,
    };
  }

  const href =
    recommendationStatus === 'ready'
      ? recommendation.sessionHref
      : buildAttemptHref({
          kind: 'training',
          languageId,
          examType: contentPack,
          skillId: selectedModuleId,
          intensity: selectedIntensity,
          level: selectedLevel || undefined,
          taskNumber: selectedTaskNumber || undefined,
        });

  return {
    action: {
      ...baseAction,
      href,
    },
    recommendedModuleId: null,
    secondaryAction: null,
    secondarySelectModuleId: null,
  };
}

export function useTrainingEntryController(
  viewer: UserLearningContext,
  readinessInput: LearnerContentReadinessInput = {},
  preferredModuleId?: TcfModuleId | null,
  preferredLevel?: string | null,
  preferredTaskNumber?: number | null
) {
  const model = useMemo(
    () => buildExamTrainingEntryPageModel(viewer, readinessInput),
    [readinessInput, viewer]
  );
  const languageId = resolveTrainingLanguageId(viewer, model.contentPack.id);
  const contentPack = model.contentPack.id;
  const levelOptions = useMemo(() => getLevelOptionsForExam(contentPack), [contentPack]);
  const writingTaskOptions = useMemo(
    () => getWritingTaskOptionsForExam(contentPack),
    [contentPack]
  );
  const resolvedPreferredModuleId =
    preferredModuleId &&
    model.moduleOptions.some((module) => module.moduleId === preferredModuleId)
      ? preferredModuleId
      : null;
  const selectedIntensity = DEFAULT_TRAINING_ENTRY_INTENSITY;
  const [selectedModuleId, setSelectedModuleId] = useState<TcfModuleId>(
    () => resolvedPreferredModuleId ?? model.selectedModuleId
  );
  const [selectedLevel, setSelectedLevelState] = useState<string>(() =>
    resolveInitialLevel(contentPack, preferredLevel)
  );
  const [selectedTaskNumber, setSelectedTaskNumber] = useState<number | null>(() => {
    if (
      preferredTaskNumber != null &&
      writingTaskOptions.some((option) => option.value === preferredTaskNumber)
    ) {
      return preferredTaskNumber;
    }
    return null;
  });

  useEffect(() => {
    if (resolvedPreferredModuleId) {
      setSelectedModuleId(resolvedPreferredModuleId);
    }
  }, [resolvedPreferredModuleId]);

  useEffect(() => {
    setSelectedLevelState(resolveInitialLevel(contentPack, preferredLevel));
  }, [contentPack, preferredLevel]);

  useEffect(() => {
    if (
      preferredTaskNumber != null &&
      writingTaskOptions.some((option) => option.value === preferredTaskNumber)
    ) {
      setSelectedTaskNumber(preferredTaskNumber);
      return;
    }
    if (
      selectedTaskNumber != null &&
      !writingTaskOptions.some((option) => option.value === selectedTaskNumber)
    ) {
      setSelectedTaskNumber(null);
    }
  }, [preferredTaskNumber, selectedTaskNumber, writingTaskOptions]);

  useEffect(() => {
    if (selectedModuleId !== 'writing') {
      setSelectedTaskNumber(null);
    }
  }, [selectedModuleId]);

  const selectLevel = useCallback(
    (level: string) => {
      const next = level.trim();
      const normalized =
        next && isLevelSupportedForExam(contentPack, next) ? next : '';
      setSelectedLevelState(normalized);
      setTrainingDifficulty(contentPack, normalized || null);
    },
    [contentPack]
  );

  const selectTaskNumber = useCallback((taskNumber: number | null) => {
    setSelectedTaskNumber(taskNumber);
  }, []);

  const selectedModule =
    model.moduleOptions.find((module) => module.moduleId === selectedModuleId) ??
    model.moduleOptions[0];
  const isOpenResponseSession = isOpenResponseSkillId(selectedModuleId);
  const showWritingTaskPicker =
    selectedModuleId === 'writing' && writingTaskOptions.length > 0;
  const selectedLevelOption =
    levelOptions.find((option) => option.value === selectedLevel) ?? levelOptions[0];
  const selectedTaskOption: WritingTaskOption | null =
    selectedTaskNumber != null
      ? (writingTaskOptions.find((option) => option.value === selectedTaskNumber) ?? null)
      : null;
  const selectedEvidenceScope = useMemo(
    () => buildLearningEvidenceRecordQueryScope(viewer, selectedModuleId),
    [selectedModuleId, viewer]
  );
  const allEvidenceScope = useMemo(() => buildLearningEvidenceRecordQueryScope(viewer), [viewer]);
  const recentAttempts = useRecentTrainingAttempts({
    userId: viewer.userId,
    ...selectedEvidenceScope,
    limit: 3,
  });
  const allRecentAttempts = useRecentTrainingAttempts({
    userId: viewer.userId,
    ...allEvidenceScope,
    limit: 30,
  });
  const trainingRecommendation = useMemo(
    () =>
      buildTrainingRecordRecommendation({
        records: allRecentAttempts.records,
        selectedModuleId,
        moduleOptions: model.moduleOptions,
        contentPack,
        selectedIntensity,
        selectedLevel,
      }),
    [
      allRecentAttempts.records,
      contentPack,
      model.moduleOptions,
      selectedIntensity,
      selectedLevel,
      selectedModuleId,
    ]
  );
  const primaryActionDecision = useMemo(
    () =>
      buildTrainingEntryPrimaryAction({
        baseAction: model.primaryAction,
        contentPack,
        languageId,
        selectedModuleId,
        selectedLevel,
        selectedTaskNumber: showWritingTaskPicker ? selectedTaskNumber : null,
        selectedModuleDisabled: selectedModule?.disabled,
        selectedModuleDisabledReason: selectedModule?.disabledReason,
        recommendation: trainingRecommendation,
        recommendationStatus: allRecentAttempts.status,
        contentReadinessStatus: model.contentReadiness.status,
      }),
    [
      allRecentAttempts.status,
      contentPack,
      languageId,
      model.primaryAction,
      model.contentReadiness.status,
      selectedLevel,
      selectedModule?.disabled,
      selectedModule?.disabledReason,
      selectedModuleId,
      selectedTaskNumber,
      showWritingTaskPicker,
      trainingRecommendation,
    ]
  );
  const runPrimaryAction = useCallback(() => {
    if (primaryActionDecision.recommendedModuleId) {
      setSelectedModuleId(primaryActionDecision.recommendedModuleId);
    }
  }, [primaryActionDecision.recommendedModuleId]);

  const startActionHandler = primaryActionDecision.recommendedModuleId
    ? runPrimaryAction
    : undefined;

  const secondaryActionHandler = useCallback(() => {
    if (primaryActionDecision.secondarySelectModuleId) {
      setSelectedModuleId(primaryActionDecision.secondarySelectModuleId);
    }
  }, [primaryActionDecision.secondarySelectModuleId]);

  const startHref = useMemo(
    () =>
      buildAttemptHref({
        kind: 'training',
        languageId,
        examType: contentPack,
        skillId: selectedModuleId,
        intensity: selectedIntensity,
        level: selectedLevel || undefined,
        taskNumber: showWritingTaskPicker ? selectedTaskNumber || undefined : undefined,
      }),
    [
      contentPack,
      languageId,
      selectedIntensity,
      selectedLevel,
      selectedModuleId,
      selectedTaskNumber,
      showWritingTaskPicker,
    ]
  );

  const incompleteCard = useIncompleteTrainingCard({
    skillId: selectedModuleId,
    startHref,
  });

  const continueIncompleteTraining = useCallback(() => {
    const href = incompleteCard.incomplete?.continueHref;
    if (!href) return;
    openQuizRuntimeHref(href);
  }, [incompleteCard.incomplete?.continueHref]);

  const discardIncompleteTraining = useCallback(() => {
    const current = incompleteCard.incomplete;
    if (!current) return;
    incompleteCard.clear();
    void (async () => {
      await abandonQuizSession(current.sessionId).catch(() => {});
      openQuizRuntimeHref(current.freshHref);
    })();
  }, [incompleteCard.clear, incompleteCard.incomplete]);

  const levelLabel = selectedLevelOption?.label ?? '不限定';
  const taskLabel = showWritingTaskPicker
    ? (selectedTaskOption?.label ?? '不限定任务')
    : null;
  const sessionSizeLabel = isOpenResponseSession
    ? OPEN_RESPONSE_SESSION_SUMMARY
    : CHOICE_SESSION_SIZE_SUMMARY;
  const configLevelPart = selectedLevel ? ` · level=${selectedLevel}` : ' · level=any';
  const configTaskPart =
    showWritingTaskPicker && selectedTaskNumber != null
      ? ` · taskNumber=${selectedTaskNumber}`
      : '';
  const configIntensityPart = isOpenResponseSession
    ? ' · openResponse=1q/20min'
    : ` · intensity=${selectedIntensity}`;

  const writingEvidenceSummary: WritingEvidenceSummary | null = useMemo(() => {
    if (selectedModuleId !== 'writing') return null;
    if (allRecentAttempts.status !== 'ready') return null;
    return summarizeWritingEvidence(allRecentAttempts.records, contentPack);
  }, [allRecentAttempts.records, allRecentAttempts.status, contentPack, selectedModuleId]);

  return {
    model,
    selectedModule,
    selectedModuleId,
    selectedIntensity,
    selectedLevel,
    selectedLevelOption,
    levelOptions,
    writingTaskOptions,
    showWritingTaskPicker,
    selectedTaskNumber,
    selectedTaskOption,
    taskLabel,
    isOpenResponseSession,
    sessionSizeLabel,
    trainingRecommendation,
    trainingRecommendationStatus: allRecentAttempts.status,
    recentAttemptRecords: recentAttempts.records,
    recentAttemptRecordsStatus: recentAttempts.status,
    incompleteTraining: incompleteCard.incomplete,
    continueIncompleteTraining,
    discardIncompleteTraining,
    writingEvidenceSummary,
    startAction: primaryActionDecision.action,
    startActionHandler,
    secondaryAction: primaryActionDecision.secondaryAction,
    secondaryActionHandler: primaryActionDecision.secondaryAction
      ? secondaryActionHandler
      : undefined,
    sessionStartHref: startHref,
    configMappingLabel: `contentPack=${contentPack} · section=${selectedModuleId} · source=accessibleQuestionBanks${configIntensityPart}${configLevelPart}${configTaskPart}`,
    selectModule: setSelectedModuleId,
    selectLevel,
    selectTaskNumber,
    levelLabel,
  };
}
